(() => {
  if (customElements.get('monoree-ring-recommendation')) return;

  class MonoreeRingRecommendation extends HTMLElement {
    connectedCallback() {
      this.picker = this.closest('variant-selects');
      this.productInfo = this.closest('product-info');
      this.sizeIndex = Number(this.dataset.sizeIndex);
      this.sizeChosen = this.picker?.dataset.monoreeSizeRequired !== 'true';
      this.signature = '';
      this.generation = (this.generation || 0) + 1;
      const generation = this.generation;
      try {
        this.variants = JSON.parse(this.querySelector('[data-monoree-ring-variants]')?.textContent);
      } catch {
        this.variants = [];
      }
      if (!Array.isArray(this.variants)) this.variants = [];

      this.onSelectionChange = (event) => {
        if (!event.target.matches('input[type="radio"], select') ||
            event.target.closest('variant-selects') !== this.picker) return;
        if (event.target.closest('[data-monoree-size-option]')) this.sizeChosen = true;
        this.refresh();
      };
      this.onChoiceClick = (event) => {
        const button = event.target.closest('[data-monoree-ring-choice]');
        if (!button || !this.contains(button) || !this.isCurrent()) return;
        event.preventDefault();
        const variant = this.recommendations().find((item) => String(item.id) === button.dataset.monoreeRingChoice);
        const target = variant && this.sizeControl(variant.options[this.sizeIndex]);
        if (!target) return;
        target.control.focus({ preventScroll: true });
        if (target.option) {
          target.control.value = target.option.value;
          target.control.dispatchEvent(new Event('change', { bubbles: true }));
        } else {
          target.control.click();
        }
      };

      this.picker?.addEventListener('change', this.onSelectionChange, true);
      this.addEventListener('click', this.onChoiceClick);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, () => this.refresh());
      }
      queueMicrotask(() => {
        if (generation === this.generation) this.refresh();
      });
    }

    disconnectedCallback() {
      this.generation += 1;
      this.picker?.removeEventListener('change', this.onSelectionChange, true);
      this.removeEventListener('click', this.onChoiceClick);
      this.unsubscribe?.();
      this.unsubscribe = null;
    }

    isCurrent() {
      return this.isConnected && this.picker?.isConnected &&
        (!this.productInfo || this.productInfo.querySelector('variant-selects') === this.picker);
    }

    groups() {
      return Array.from(this.picker?.children || []).filter((element) =>
        element.matches('fieldset, .product-form__input--dropdown'));
    }

    selection() {
      return this.groups().map((group) => {
        const select = group.querySelector('select');
        return select ? select.value : group.querySelector('input[type="radio"]:checked')?.value;
      });
    }

    sizeControl(value) {
      const group = this.groups()[this.sizeIndex];
      const select = group?.querySelector('select');
      if (select) {
        const option = Array.from(select.options).find((item) =>
          item.value === String(value) && !item.disabled && item.dataset.optionValueId);
        return !select.disabled && option ? { control: select, option } : null;
      }
      const control = Array.from(group?.querySelectorAll('input[type="radio"]') || []).find((input) =>
        input.value === String(value) && !input.disabled && input.dataset.optionValueId);
      return control ? { control } : null;
    }

    recommendations() {
      const sizing = window.MonoreeRingSizing;
      if (!this.isCurrent() || !this.sizeChosen || !sizing ||
          !Number.isInteger(this.sizeIndex) || this.sizeIndex < 0) return [];
      const options = this.selection();
      if (!options.length || options.some((value) => !value)) return [];
      const target = sizing.parseSize(options[this.sizeIndex]);
      if (target === null) return [];
      const exactInStock = this.variants.some((variant) => variant.available === true &&
        variant.inStock === true && Array.isArray(variant.options) &&
        variant.options.length === options.length &&
        variant.options.every((value, index) => String(value) === options[index]));
      if (exactInStock) return [];

      const sizes = new Set();
      return sizing.nearest(this.variants, target, { sizeIndex: this.sizeIndex, options }).filter((variant) => {
        const size = sizing.parseSize(variant.options[this.sizeIndex]);
        if (sizes.has(size) || !this.sizeControl(variant.options[this.sizeIndex])) return false;
        sizes.add(size);
        return true;
      }).slice(0, 2);
    }

    refresh() {
      const candidates = this.recommendations();
      this.hidden = candidates.length === 0;
      window.MonoreeRestockStatus?.update(this.productInfo);
      const target = window.MonoreeRingSizing?.parseSize(this.selection()[this.sizeIndex]);
      const signature = `${target}|${candidates.map((variant) => `${variant.id}:${variant.options[this.sizeIndex]}`).join('|')}`;
      if (signature === this.signature) return;
      this.signature = signature;
      this.querySelectorAll('[data-monoree-ring-choice]').forEach((button) => button.remove());
      const numberFormatter = new Intl.NumberFormat(document.documentElement.lang || 'lt', {
        maximumFractionDigits: 2,
        useGrouping: false,
      });
      candidates.forEach((variant) => {
        const size = window.MonoreeRingSizing.parseSize(variant.options[this.sizeIndex]);
        const label = numberFormatter.format(size / 100);
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'monoree-ring-choice';
        button.dataset.monoreeRingChoice = String(variant.id);
        button.textContent = label;
        button.setAttribute('aria-label', (this.dataset.chooseLabel || '__SIZE__').replace('__SIZE__', label));
        (this.querySelector('[data-monoree-ring-choices]') || this.querySelector('[data-monoree-ring-copy]') || this).appendChild(button);
      });
      const explanation = this.querySelector('[data-monoree-ring-difference]');
      if (explanation) {
        const candidateSize = candidates.length
          ? window.MonoreeRingSizing.parseSize(candidates[0].options[this.sizeIndex]) : null;
        const difference = candidateSize !== null && target !== null ? Math.abs(candidateSize - target) : 0;
        explanation.hidden = difference === 0 || !this.dataset.differenceLabel;
        explanation.textContent = explanation.hidden ? '' : this.dataset.differenceLabel
          .replace('__DIFFERENCE__', numberFormatter.format(difference / 100));
      }
    }
  }

  customElements.define('monoree-ring-recommendation', MonoreeRingRecommendation);
})();
